import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Lock, ShieldCheck, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth, Order } from '../context/AuthContext';
import { FALLBACK_PRODUCT_IMAGE } from '../data/products';
export function Checkout() {
  const {
    state,
    cartTotal,
    dispatch
  } = useCart();
  const {
    user,
    isAuthenticated,
    register,
    addOrder
  } = useAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [shippingMethod, setShippingMethod] = useState<'standard' | 'express'>('standard');
  // Form state — prefill from user where available
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [address, setAddress] = useState('');
  const [island, setIsland] = useState('');
  const [postalCode, setPostalCode] = useState('');
  // Optional registration during checkout
  const [createAccount, setCreateAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState('');
  const [registerError, setRegisterError] = useState<string | null>(null);
  const shippingCost = shippingMethod === 'standard' ? 0 : 50;
  const finalTotal = cartTotal + shippingCost;
  if (state.items.length === 0 && !isProcessing) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center" data-id="element-297">
        <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-298">
          Your cart is empty
        </h2>
        <button type="button" onClick={() => navigate('/shop')} className="text-red-600 hover:underline" data-id="element-299">
          Return to Shop
        </button>
      </div>;
  }
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError(null);
    // If guest opted to create an account, do that first
    if (!isAuthenticated && createAccount) {
      if (accountPassword.length < 6) {
        setRegisterError('Password must be at least 6 characters.');
        return;
      }
      const result = register({
        email,
        password: accountPassword,
        firstName,
        lastName,
        phone
      });
      if (!result.success) {
        setRegisterError(result.error || 'Could not create account.');
        return;
      }
    }
    setIsProcessing(true);
    setTimeout(() => {
      const orderNumber = `BS-${Math.floor(10000 + Math.random() * 90000)}`;
      const order: Order = {
        id: orderNumber,
        date: new Date().toISOString(),
        items: state.items.map(item => ({
          productId: item.productId,
          productName: item.product.name,
          productImage: item.product.images[0],
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          selectedSize: item.selectedSize,
          selectedColor: item.selectedColor?.name,
          selectedAddOns: item.selectedAddOns.map(a => a.name)
        })),
        subtotal: cartTotal,
        shipping: shippingCost,
        total: finalTotal,
        status: 'processing',
        shippingAddress: {
          firstName,
          lastName,
          address,
          island,
          postalCode
        },
        email,
        phone
      };
      // Save to user account if logged in (or just-registered)
      if (isAuthenticated || createAccount) addOrder(order);
      dispatch({
        type: 'CLEAR_CART'
      });
      navigate('/order-confirmation', {
        state: {
          orderNumber,
          total: finalTotal,
          hasAccount: isAuthenticated || createAccount
        }
      });
    }, 2500);
  };
  if (isProcessing) {
    return <div className="min-h-[80vh] flex flex-col items-center justify-center bg-gray-50" data-id="element-300">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center max-w-md w-full" data-id="element-301">
          <img src="https://cdn.magicpatterns.com/uploads/2e3rWSAHwSB4NuxAx1sgGW/Logo_of_Bank_of_Maldives.svg.png" alt="Bank of Maldives" className="w-16 h-16 object-contain mx-auto mb-4" data-id="element-302" />
          <div className="w-10 h-10 mx-auto mb-6" data-id="element-303">
            <div className="w-10 h-10 border-4 border-red-200 border-t-red-600 rounded-full animate-spin" data-id="element-304"></div>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-305">
            Redirecting to Bank of Maldives…
          </h2>
          <p className="text-gray-500 text-sm" data-id="element-306">
            Please do not close this window or press back.
          </p>
          <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-center gap-2 text-gray-400" data-id="element-307">
            <Lock className="w-4 h-4" data-id="element-308" />
            <span className="text-xs font-medium uppercase tracking-wider" data-id="element-309">
              Secure Payment Gateway
            </span>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-gray-50 min-h-screen pb-12" data-id="element-310">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-311">
        <Link to="/cart" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 mb-6" data-id="element-312">
          <ChevronLeft className="w-4 h-4 mr-1" data-id="element-313" /> Back to Cart
        </Link>

        {/* Sign-in CTA for guests */}
        {!isAuthenticated && <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-314">
            <div className="text-sm text-gray-700" data-id="element-315">
              <span className="font-medium" data-id="element-316">Already have an account?</span>{' '}
              <span className="text-gray-500" data-id="element-317">
                Sign in for faster checkout.
              </span>
            </div>
            <Link to="/login" state={{
          from: '/checkout'
        }} className="text-sm font-medium text-red-600 hover:text-red-700" data-id="element-318">
              Sign In →
            </Link>
          </div>}

        {isAuthenticated && <div className="bg-green-50 border border-green-100 rounded-2xl p-4 mb-6 flex items-center gap-3" data-id="element-319">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-320" />
            <div className="text-sm" data-id="element-321">
              <span className="font-medium text-gray-900" data-id="element-322">
                Checking out as {user?.firstName}
              </span>
              <span className="text-gray-500" data-id="element-323"> ({user?.email})</span>
            </div>
          </div>}

        <div className="lg:grid lg:grid-cols-12 lg:gap-x-12" data-id="element-324">
          <div className="lg:col-span-7 xl:col-span-8" data-id="element-325">
            <form id="checkout-form" onSubmit={handleSubmit} className="space-y-6" data-id="element-326">
              {/* Contact Info */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-327">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-328">
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-329">
                  <div className="sm:col-span-2" data-id="element-330">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-331">
                      Email address
                    </label>
                    <input type="email" id="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-332" />
                  </div>
                  <div className="sm:col-span-2" data-id="element-333">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-334">
                      Phone number
                    </label>
                    <div className="flex" data-id="element-335">
                      <span className="inline-flex items-center px-4 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-336">
                        +960
                      </span>
                      <input type="tel" id="phone" required placeholder="7XXXXXX" pattern="[79][0-9]{6}" value={phone} onChange={e => setPhone(e.target.value)} className="flex-1 min-w-0 block w-full px-4 py-2 rounded-none rounded-r-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-337" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-338">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-339">
                  Shipping Address
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-340">
                  <div data-id="element-341">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-342">
                      First name
                    </label>
                    <input type="text" required value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-343" />
                  </div>
                  <div data-id="element-344">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-345">
                      Last name
                    </label>
                    <input type="text" required value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-346" />
                  </div>
                  <div className="sm:col-span-2" data-id="element-347">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-348">
                      Address
                    </label>
                    <input type="text" required placeholder="House name, Street" value={address} onChange={e => setAddress(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-349" />
                  </div>
                  <div data-id="element-350">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-351">
                      Island / City
                    </label>
                    <select required value={island} onChange={e => setIsland(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white" data-id="element-352">
                      <option value="" data-id="element-353">Select an island...</option>
                      <option value="Malé" data-id="element-354">Malé</option>
                      <option value="Hulhumalé" data-id="element-355">Hulhumalé</option>
                      <option value="Villingili" data-id="element-356">Villingili</option>
                      <option value="Addu City" data-id="element-357">Addu City</option>
                      <option value="Fuvahmulah" data-id="element-358">Fuvahmulah</option>
                      <option value="Kulhudhuffushi" data-id="element-359">Kulhudhuffushi</option>
                    </select>
                  </div>
                  <div data-id="element-360">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-361">
                      Postal code
                    </label>
                    <input type="text" value={postalCode} onChange={e => setPostalCode(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-362" />
                  </div>
                </div>
              </div>

              {/* Shipping Method */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-363">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-364">
                  Shipping Method
                </h2>
                <div className="space-y-3" data-id="element-365">
                  <label className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-colors ${shippingMethod === 'standard' ? 'border-red-600 bg-red-50/30' : 'border-gray-200'}`} data-id="element-366">
                    <div className="flex items-center gap-3" data-id="element-367">
                      <input type="radio" name="shipping" value="standard" checked={shippingMethod === 'standard'} onChange={() => setShippingMethod('standard')} className="w-4 h-4 text-red-600 focus:ring-red-500 border-gray-300" data-id="element-368" />
                      <div data-id="element-369">
                        <span className="block text-sm font-medium text-gray-900" data-id="element-370">
                          Standard Delivery
                        </span>
                        <span className="block text-xs text-gray-500" data-id="element-371">
                          2-3 business days
                        </span>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900" data-id="element-372">
                      Free
                    </span>
                  </label>

                  <label className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-colors ${shippingMethod === 'express' ? 'border-red-600 bg-red-50/30' : 'border-gray-200'}`} data-id="element-373">
                    <div className="flex items-center gap-3" data-id="element-374">
                      <input type="radio" name="shipping" value="express" checked={shippingMethod === 'express'} onChange={() => setShippingMethod('express')} className="w-4 h-4 text-red-600 focus:ring-red-500 border-gray-300" data-id="element-375" />
                      <div data-id="element-376">
                        <span className="block text-sm font-medium text-gray-900" data-id="element-377">
                          Express Delivery
                        </span>
                        <span className="block text-xs text-gray-500" data-id="element-378">
                          Same day / Next day
                        </span>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900" data-id="element-379">
                      MVR 50.00
                    </span>
                  </label>
                </div>
              </div>

              {/* Create account (guests only) */}
              {!isAuthenticated && <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-380">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-381">
                    <input type="checkbox" checked={createAccount} onChange={e => setCreateAccount(e.target.checked)} className="w-4 h-4 mt-1 text-red-600 rounded border-gray-300 focus:ring-red-500" data-id="element-382" />
                    <div className="flex-1" data-id="element-383">
                      <span className="text-sm font-medium text-gray-900" data-id="element-384">
                        Create an account for faster checkout
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5" data-id="element-385">
                        Save your details, track orders, and re-order in one
                        click. Or continue as a guest.
                      </span>
                    </div>
                  </label>

                  {createAccount && <div className="mt-4 pl-7" data-id="element-386">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-387">
                        Create a password
                      </label>
                      <input type="password" minLength={6} required={createAccount} value={accountPassword} onChange={e => setAccountPassword(e.target.value)} placeholder="At least 6 characters" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-388" />
                      {registerError && <p className="mt-2 text-sm text-red-600" data-id="element-389">
                          {registerError}
                        </p>}
                    </div>}
                </div>}

              {/* Payment Section */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-390">
                <div className="flex items-center gap-2 mb-4" data-id="element-391">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-392">
                    Payment
                  </h2>
                  <Lock className="w-4 h-4 text-gray-400" data-id="element-393" />
                </div>
                <p className="text-sm text-gray-500 mb-4" data-id="element-394">
                  All transactions are secure and encrypted.
                </p>

                <div className="border border-red-200 bg-red-50/30 rounded-xl p-5" data-id="element-395">
                  <div className="flex items-start gap-4" data-id="element-396">
                    <input type="radio" checked readOnly className="w-4 h-4 mt-1 text-red-600 focus:ring-red-500 border-gray-300 flex-shrink-0" data-id="element-397" />
                    <div className="flex-1" data-id="element-398">
                      <div className="flex items-center gap-3 mb-2" data-id="element-399">
                        <img src="https://cdn.magicpatterns.com/uploads/2e3rWSAHwSB4NuxAx1sgGW/Logo_of_Bank_of_Maldives.svg.png" alt="Bank of Maldives" className="w-10 h-10 object-contain" data-id="element-400" />
                        <div data-id="element-401">
                          <div className="font-medium text-gray-900" data-id="element-402">
                            Bank of Maldives Gateway
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-403">
                            Pay securely with BML
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-3 mt-3" data-id="element-404">
                        You will be redirected to the secure BML payment gateway
                        to complete your purchase.
                      </p>
                      <div className="inline-flex items-center gap-2 text-xs font-medium text-gray-500 bg-white px-3 py-1.5 rounded-md border border-gray-200" data-id="element-405">
                        <ShieldCheck className="w-4 h-4 text-green-600" data-id="element-406" />
                        BML Secure Checkout
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-5 xl:col-span-4 mt-8 lg:mt-0" data-id="element-407">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 sticky top-24" data-id="element-408">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-409">
                Order Summary
              </h2>

              <div className="flow-root mb-6" data-id="element-410">
                <ul className="-my-4 divide-y divide-gray-100" data-id="element-411">
                  {state.items.map(item => <li key={item.id} className="py-4 flex gap-4" data-id="element-412">
                      <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden border border-gray-200 flex-shrink-0 relative" data-id="element-413">
                        <img src={item.product.images[0]} alt={item.product.name} onError={e => {
                      const el = e.currentTarget;
                      if (el.src !== FALLBACK_PRODUCT_IMAGE) el.src = FALLBACK_PRODUCT_IMAGE;
                    }} className="w-full h-full object-cover" data-id="element-414" />
                        <span className="absolute -top-2 -right-2 w-5 h-5 bg-gray-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white" data-id="element-415">
                          {item.quantity}
                        </span>
                      </div>
                      <div className="flex-1 flex flex-col justify-center" data-id="element-416">
                        <h3 className="text-sm font-medium text-gray-900 line-clamp-1" data-id="element-417">
                          {item.product.name}
                        </h3>
                        <div className="text-xs text-gray-500 mt-0.5" data-id="element-418">
                          {item.selectedSize && <span data-id="element-419">Size: {item.selectedSize}</span>}
                          {item.selectedSize && item.selectedColor && <span data-id="element-420"> | </span>}
                          {item.selectedColor && <span data-id="element-421">Color: {item.selectedColor.name}</span>}
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-900 flex items-center" data-id="element-422">
                        MVR {(item.unitPrice * item.quantity).toFixed(2)}
                      </div>
                    </li>)}
                </ul>
              </div>

              <div className="space-y-3 border-t border-gray-100 pt-4 mb-6" data-id="element-423">
                <div className="flex justify-between text-sm text-gray-600" data-id="element-424">
                  <span data-id="element-425">Subtotal</span>
                  <span data-id="element-426">MVR {cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600" data-id="element-427">
                  <span data-id="element-428">Shipping</span>
                  <span data-id="element-429">
                    {shippingCost === 0 ? 'Free' : `MVR ${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between text-base font-bold text-gray-900 pt-3 border-t border-gray-100" data-id="element-430">
                  <span data-id="element-431">Total</span>
                  <span data-id="element-432">MVR {finalTotal.toFixed(2)}</span>
                </div>
              </div>

              <button type="submit" form="checkout-form" className="w-full bg-red-600 text-white py-4 px-4 rounded-xl font-medium hover:bg-red-700 transition-colors flex justify-center items-center gap-2 shadow-sm" data-id="element-433">
                <Lock className="w-4 h-4" data-id="element-434" />
                Checkout with Bank of Maldives
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}